import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, MapPin, Users, Mail, Phone, Plus, Search, Wrench, Home } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import { Client, StepClientProps, ClientContact, CreateClientForm, ClientLocationDto, EquipmentDto, UnitDto } from '../../types';
import { formatPhoneNumber } from '../../utils/formatters';
import Modal from '../ui/Modal';
import ClientCreationWizard from '../clients/ClientCreationWizard';
import { useDebounce } from '../../hooks/useDebounce';

// PropertyManagement = 9
const PROPERTY_MANAGEMENT_COMPANY_TYPE = 9;

const StepClient: React.FC<StepClientProps> = ({ 
  data, 
  onChange, 
  onNext, 
  isFirstStep, 
  isLastStep, 
  isSubmitting 
}) => {
  // Local state for grid
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500); // 500ms delay
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const debouncedLocationSearchTerm = useDebounce(locationSearchTerm, 300); // 300ms delay for locations
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formError, setFormError] = useState<string>('');
  
  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Queries
  const {
    data: clientsResponse,
    isLoading: clientsLoading,
    error: clientsError
  } = useQuery({
    queryKey: ['clients', currentPage, pageSize, debouncedSearchTerm, sortField, sortDirection],
    queryFn: () => apiService.getClients(currentPage, pageSize, debouncedSearchTerm, sortField, sortDirection),
  });

  const {
    data: categoriesResponse,
    isLoading: categoriesLoading,
    error: categoriesError
  } = useQuery({
    queryKey: ['categories', 'active'],
    queryFn: () => {
      console.log('Fetching active categories...');
      return apiService.getActiveCategories();
    },
    retry: 1,
    retryDelay: 1000
  });

  // Fetch primary contact for selected client
  const {
    data: primaryContactResponse,
    isLoading: primaryContactLoading
  } = useQuery({
    queryKey: ['clientPrimaryContact', data.selectedClient?.id],
    queryFn: () => apiService.getPrimaryClientContact(data.selectedClient!.id),
    enabled: !!data.selectedClient,
    retry: 1
  });

  // Data
  const clients = clientsResponse?.data?.data || [];
  const totalCount = clientsResponse?.data?.totalCount || 0;
  const totalPages = clientsResponse?.data?.totalPages || 1;
  const categories = categoriesResponse?.data || []; // getActiveCategories returns direct array, not paged
  const primaryContact = primaryContactResponse?.data;

  // Create client + primary location mutation (matches clients page modal behavior)
  const createClientWithLocationMutation = useMutation({
    mutationFn: async ({ client, location }: { client: CreateClientForm; location: Partial<ClientLocationDto>; }) => {
      const clientResp = await apiService.createClient(client);
      if (!clientResp.success || !clientResp.data) {
        throw new Error(clientResp.message || 'Failed to create client');
      }
      const clientId = clientResp.data.id;
      const locResp = await apiService.createClientLocation(clientId, location);
      return { client: clientResp.data, location: locResp.data };
    },
    onSuccess: async (result) => {
      toast.success(`Client "${result.client.name}" created with primary location!`);
      setShowCreateModal(false);
      setFormError('');
      // Refresh client list
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      // Auto-select new client and its location in the wizard
      onChange({
        selectedClient: result.client,
        selectedClientLocationId: result.location?.id,
        selectedClientLocationName: result.location?.locationName,
        selectedClientLocationCity: result.location?.city,
        selectedClientLocationState: result.location?.state,
        selectedClientLocationZipCode: result.location?.zipCode
      });
    },
    onError: (error: any) => {
      console.error('Client creation error:', error);
      setFormError(error.response?.data?.message || error.message || 'Failed to create client');
    }
  });

  // Handlers
  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleClientSelect = useCallback((client: Client) => {
    console.log('Client selected:', client);
    onChange({ 
      selectedClient: client,
      selectedClientLocationId: client.primaryLocationId,
      selectedClientLocationName: client.primaryLocationName?.replace(' - Primary Location','') || undefined,
      selectedClientLocationCity: client.primaryLocationCity || client.city,
      selectedClientLocationState: client.primaryLocationState || client.state,
      selectedClientLocationZipCode: client.primaryLocationZipCode || client.zipCode,
      zipcode: (client.primaryLocationZipCode || client.zipCode || data.zipcode || '')
    });
  }, [onChange, data.zipcode]);

  const handleZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ zipcode: e.target.value });
  };

  const handleRadiusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ radius: Number(e.target.value) });
  };

  const handleCategoryChange = (categoryId: number, checked: boolean) => {
    const currentCategories = data.selectedCategories || [];
    const newCategories = checked 
      ? [...currentCategories, categoryId]
      : currentCategories.filter(id => id !== categoryId);
    onChange({ selectedCategories: newCategories });
  };

  const allCategoryIds = useMemo(() => categories.map((c: any) => c.id), [categories]);
  const allCategoriesSelected = (data.selectedCategories?.length || 0) > 0 && data.selectedCategories!.length === allCategoryIds.length;
  const handleSelectAllCategories = (checked: boolean) => {
    onChange({ selectedCategories: checked ? allCategoryIds : [] });
  };

  const handleCreateClient = (clientData: CreateClientForm) => {
    createClientMutation.mutate(clientData);
  };

  const handleNext = () => {
    console.log('Step 1 data:', data);
    // Validation
    if (!data.selectedClient) {
      toast.error('Please select a client');
      return;
    }
    if (!data.selectedClientLocationId) {
      toast.error('Please select a client location');
      return;
    }
    if (!data.radius) {
      toast.error('Please select a radius');
      return;
    }
    if (!data.selectedCategories || data.selectedCategories.length === 0) {
      toast.error('Please select at least one category');
      return;
    }
    console.log('Step 1 validation passed, proceeding to next step');
    onNext();
  };



  // Load locations for selected client
  const { data: locationsResponse, isLoading: locationsLoading } = useQuery({
    queryKey: ['clientLocations', data.selectedClient?.id],
    queryFn: async () => {
      const res = await apiService.getClientLocations(data.selectedClient!.id);
      return res.data as ClientLocationDto[];
    },
    enabled: !!data.selectedClient?.id,
    staleTime: 60_000,
    retry: 1
  });
  const locations: ClientLocationDto[] = locationsResponse || [];
  const selectedLocation = data.selectedClientLocationId ? locations.find(l => l.id === data.selectedClientLocationId) : undefined;

  // Filter locations based on search term
  const filteredLocations = useMemo(() => {
    if (!debouncedLocationSearchTerm.trim()) {
      return locations;
    }
    const searchLower = debouncedLocationSearchTerm.toLowerCase();
    return locations.filter((loc) => {
      const locationName = loc.locationName?.toLowerCase() || '';
      const city = loc.city?.toLowerCase() || '';
      const state = loc.state?.toLowerCase() || '';
      const zipCode = loc.zipCode?.toLowerCase() || '';
      const address = loc.address?.toLowerCase() || '';
      const contactName = loc.contactName?.toLowerCase() || '';
      const contactPhone = loc.contactPhone?.toLowerCase() || '';
      const contactEmail = loc.contactEmail?.toLowerCase() || '';
      
      return (
        locationName.includes(searchLower) ||
        city.includes(searchLower) ||
        state.includes(searchLower) ||
        zipCode.includes(searchLower) ||
        address.includes(searchLower) ||
        contactName.includes(searchLower) ||
        contactPhone.includes(searchLower) ||
        contactEmail.includes(searchLower)
      );
    });
  }, [locations, debouncedLocationSearchTerm]);

  // Load equipment for selected location
  const { data: equipmentResponse, isLoading: equipmentLoading } = useQuery({
    queryKey: ['equipment-by-location', data.selectedClientLocationId],
    queryFn: async () => {
      const res = await apiService.getEquipmentByLocation(data.selectedClientLocationId!);
      return res.data || [];
    },
    enabled: !!data.selectedClientLocationId,
    staleTime: 60_000,
    retry: 1
  });
  const equipmentList: EquipmentDto[] = equipmentResponse || [];

  // Fetch company to check company type
  const { data: companyResponse } = useQuery({
    queryKey: ['company'],
    queryFn: () => apiService.getCompany(),
  });
  const companyType = companyResponse?.data?.companyType ?? 0; // Default to Facilities
  const isPropertyManagement = companyType === PROPERTY_MANAGEMENT_COMPANY_TYPE;

  // Load units for selected location (only for Property Management companies)
  const { data: unitsResponse, isLoading: unitsLoading } = useQuery({
    queryKey: ['units-by-location', data.selectedClientLocationId],
    queryFn: async () => {
      const res = await apiService.getUnitsByLocation(data.selectedClientLocationId!);
      return res.data || [];
    },
    enabled: !!data.selectedClientLocationId && isPropertyManagement,
    staleTime: 60_000,
    retry: 1
  });
  const unitsList: UnitDto[] = unitsResponse || [];

  const handleEquipmentSelect = useCallback((equipmentId: number | undefined) => {
    onChange({ equipmentId });
  }, [onChange]);

  const handleUnitSelect = useCallback((unitId: number | undefined) => {
    onChange({ unitId });
  }, [onChange]);

  // Error handling
  if (clientsError) {
    console.error('Clients error:', clientsError);
    return (
      <div className="text-center py-8">
        <div className="text-red-600 text-lg font-medium">Error loading clients</div>
        <div className="text-gray-500 mt-2">
          {clientsError instanceof Error ? clientsError.message : 'Please try again'}
        </div>
        {(clientsError as any)?.response?.data?.message && (
          <div className="text-gray-500 mt-1 text-sm">
            {(clientsError as any).response.data.message}
          </div>
        )}
      </div>
    );
  }

  if (categoriesError) {
    console.error('Categories error:', categoriesError);
  }

  return (
    <div className="space-y-6">
      {/* Client Selection */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Select Client</h3>
          <button
            onClick={() => { setShowCreateModal(true); setFormError(''); }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Create Client</span>
          </button>
        </div>
        <div className="relative mb-3">
          <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder="Search by name, address, phone, email, or location..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color"
          />
        </div>
        <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
          {clientsLoading ? (
            <div className="p-3 text-sm text-gray-500">Loading clients…</div>
          ) : clients.length === 0 ? (
            <div className="p-3 text-sm text-gray-500">No clients found</div>
          ) : (
            clients.map((c: Client) => (
              <label key={c.id} className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                <div>
                  <div className="font-medium text-gray-900 dark:text-gray-100">{c.name}</div>
                  {c.primaryLocationName && (
                    <>
                      <div className="text-xs text-gray-600 dark:text-gray-300">
                        {c.primaryLocationName.replace(' - Primary Location','')}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {[c.primaryLocationCity, c.primaryLocationState, c.primaryLocationZipCode].filter(Boolean).join(', ')}
                      </div>
                    </>
                  )}
                  <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                    {c.phone && (
                      <span className="inline-flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {formatPhoneNumber(c.phone)}
                      </span>
                    )}
                    {c.email && (
                      <span className="inline-flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {c.email}
                      </span>
                    )}
                  </div>
                </div>
                <input
                  type="radio"
                  name="clientSelection"
                  checked={data.selectedClient?.id === c.id}
                  onChange={() => handleClientSelect(c)}
                  className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600"
                />
              </label>
            ))
          )}
        </div>
        {totalPages > 1 && (
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            >Prev</button>
            <div className="text-sm text-gray-600 dark:text-gray-400 px-1">Page {currentPage} of {totalPages}</div>
            <button
              onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            >Next</button>
          </div>
        )}
      </div>

      {/* Location Selection (after client chosen) */}
      {data.selectedClient && (
        <div>
          <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-2">Select Location</h4>
          <div className="relative mb-3">
            <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              value={locationSearchTerm}
              onChange={(e) => setLocationSearchTerm(e.target.value)}
              placeholder="Search locations by name, address, city, state, zip, or contact..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color"
            />
          </div>
          <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {locationsLoading ? (
              <div className="p-3 text-sm text-gray-500">Loading locations…</div>
            ) : filteredLocations.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">
                {locations.length === 0 
                  ? 'No locations found for this client' 
                  : `No locations match "${locationSearchTerm}"`}
              </div>
            ) : (
              filteredLocations.map((loc) => (
                <label key={loc.id} className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">{loc.locationName?.replace(' - Primary Location','')}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {[loc.city, loc.state, loc.zipCode].filter(Boolean).join(', ')}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                      {loc.contactPhone && (
                        <span className="inline-flex items-center gap-1">
                          <Phone className="h-3 w-3" /> {formatPhoneNumber(loc.contactPhone)}
                        </span>
                      )}
                      {loc.contactEmail && (
                        <span className="inline-flex items-center gap-1">
                          <Mail className="h-3 w-3" /> {loc.contactEmail}
                        </span>
                      )}
                    </div>
                  </div>
                  <input
                    type="radio"
                    name="clientLocationSelection"
                    checked={data.selectedClientLocationId === loc.id}
                    onChange={() => {
                      onChange({
                        selectedClientLocationId: loc.id,
                        selectedClientLocationName: loc.locationName,
                        selectedClientLocationCity: loc.city,
                        selectedClientLocationState: loc.state,
                        selectedClientLocationZipCode: loc.zipCode,
                        zipcode: loc.zipCode || data.zipcode || '',
                        equipmentId: undefined, // Reset equipment when location changes
                        unitId: undefined // Reset unit when location changes
                      });
                    }}
                    className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600"
                  />
                </label>
              ))
            )}
          </div>
        </div>
      )}

      {/* Equipment Selection (after location chosen) */}
      {data.selectedClientLocationId && (
        <div>
          <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-2">
            <Wrench className="inline h-4 w-4 mr-1" />
            Select Equipment (Optional)
          </h4>
          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {equipmentLoading ? (
              <div className="p-3 text-sm text-gray-500">Loading equipment…</div>
            ) : equipmentList.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">
                No equipment found for this location. Equipment can be added later.
              </div>
            ) : (
              <>
                <label className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">None</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">No equipment selected</div>
                  </div>
                  <input
                    type="radio"
                    name="equipmentSelection"
                    checked={!data.equipmentId}
                    onChange={() => handleEquipmentSelect(undefined)}
                    className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600"
                  />
                </label>
                {equipmentList.map((equipment) => (
                  <label key={equipment.id} className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div>
                      <div className="flex items-center gap-2">
                        <span>{equipment.equipmentTypeIcon}</span>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{equipment.name}</div>
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {equipment.equipmentTypeDisplay}
                        {equipment.make && ` • ${equipment.make}`}
                        {equipment.model && ` ${equipment.model}`}
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="equipmentSelection"
                      checked={data.equipmentId === equipment.id}
                      onChange={() => handleEquipmentSelect(equipment.id)}
                      className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600"
                    />
                  </label>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Unit Selection (after location chosen) - Only for Property Management companies */}
      {data.selectedClientLocationId && isPropertyManagement && (
        <div>
          <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-2">
            <Home className="inline h-4 w-4 mr-1" />
            Select Unit (Optional)
          </h4>
          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-md divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {unitsLoading ? (
              <div className="p-3 text-sm text-gray-500">Loading units…</div>
            ) : unitsList.length === 0 ? (
              <div className="p-3 text-sm text-gray-500">
                No units found for this location. Units can be added later.
              </div>
            ) : (
              <>
                <label className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-gray-100">None</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">No unit selected</div>
                  </div>
                  <input
                    type="radio"
                    name="unitSelection"
                    checked={!data.unitId}
                    onChange={() => handleUnitSelect(undefined)}
                    className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600"
                  />
                </label>
                {unitsList.map((unit) => (
                  <label key={unit.unitId} className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">Unit {unit.unitNumber}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {unit.unitType || ''}
                        {unit.bedrooms !== null && unit.bedrooms !== undefined && ` • ${unit.bedrooms} BR`}
                        {unit.bathrooms && ` / ${unit.bathrooms} BA`}
                        {unit.squareFeet && ` • ${unit.squareFeet} sq ft`}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Status: {unit.statusDisplay}
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="unitSelection"
                      checked={data.unitId === unit.unitId}
                      onChange={() => handleUnitSelect(unit.unitId)}
                      className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600"
                    />
                  </label>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* Guidance for next inputs */}
      <div className="rounded-md bg-accent-color-50 dark:bg-accent-color-900/20 border border-accent-color-200 dark:border-accent-color-700 p-3 text-sm text-accent-color-800 dark:text-accent-color-200">
        Please select a search radius and one or more service categories to continue to the next step.
      </div>

      {/* Additional Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Zipcode */}
        <div>
          <label htmlFor="zipcode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <MapPin className="inline h-4 w-4 mr-1" />
            Zipcode (Vendor Search Area) <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="zipcode"
            value={data.zipcode || ''}
            onChange={handleZipcodeChange}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color placeholder-gray-400 dark:placeholder-gray-500"
            placeholder="Enter zipcode"
          />
          {data.selectedClient && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {selectedLocation ? (
                <>Default (from selected location): {selectedLocation.zipCode || '-'}</>
              ) : (
                <>Default (from primary location): {(data.selectedClient.primaryLocationZipCode || data.selectedClient.zipCode || '-') }</>
              )}
            </p>
          )}
        </div>

        {/* Radius */}
        <div>
          <label htmlFor="radius" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search Radius <span className="text-red-500">*</span>
          </label>
          <select
            id="radius"
            value={data.radius || ''}
            onChange={handleRadiusChange}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-accent-color focus:border-accent-color"
          >
            <option value="">Select radius</option>
            <option value={25}>25 miles</option>
            <option value={50}>50 miles</option>
            <option value={75}>75 miles</option>
            <option value={100}>100 miles</option>
            <option value={200}>200 miles</option>
          </select>
        </div>

        {/* Selected Client & Location Info */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Selected Client
          </label>
          {data.selectedClient ? (
            <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-md">
              <div className="font-medium text-green-800 dark:text-green-200">{data.selectedClient.name}</div>
              {selectedLocation ? (
                <div className="mt-1">
                  <div className="text-xs text-green-700 dark:text-green-300">
                    {selectedLocation.locationName?.replace(' - Primary Location','')}
                  </div>
                  <div className="text-sm text-green-600 dark:text-green-400">
                    {[selectedLocation.city, selectedLocation.state, selectedLocation.zipCode]
                      .filter(Boolean)
                      .join(', ')}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-green-600 dark:text-green-400">
                  {[data.selectedClient.primaryLocationCity || data.selectedClient.city, data.selectedClient.primaryLocationState || data.selectedClient.state, data.selectedClient.primaryLocationZipCode || data.selectedClient.zipCode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
              {primaryContact && (
                <div className="mt-2 pt-2 border-t border-green-200">
                  <div className="flex items-center space-x-1 text-xs text-green-700 dark:text-green-300">
                    <Users className="h-3 w-3" />
                    <span className="font-medium">Primary Contact:</span>
                  </div>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                    <div className="font-medium">{primaryContact.fullName}</div>
                    {primaryContact.title && (
                      <div>{primaryContact.title}</div>
                    )}
                    {primaryContact.email && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Mail className="h-3 w-3" />
                        <a href={`mailto:${primaryContact.email}`} className="hover:underline text-green-600 dark:text-green-400">
                          {primaryContact.email}
                        </a>
                      </div>
                    )}
                    {primaryContact.phone && (
                      <div className="flex items-center space-x-1 mt-1">
                        <Phone className="h-3 w-3" />
                        <span>{primaryContact.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400">
              No client selected
            </div>
          )}
        </div>
      </div>

                   {/* Categories */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Service Categories (Select at least one)
          </label>
          {categories.length > 0 && (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
              <input
                type="checkbox"
                checked={allCategoriesSelected}
                onChange={(e) => handleSelectAllCategories(e.target.checked)}
                className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600 rounded"
              />
              <span>Select all</span>
            </label>
          )}
        </div>
        {categoriesLoading ? (
          <div className="animate-pulse">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        ) : categoriesError ? (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <div className="text-red-600 text-sm">Error loading categories: {categoriesError.message}</div>
          </div>
        ) : categories.length === 0 ? (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
            <div className="text-gray-500 text-sm">No active categories found</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-h-48 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-white dark:bg-gray-700">
            {categories.map((category) => (
              <label 
                key={category.id} 
                className="flex items-center space-x-2 cursor-pointer hover:opacity-80"
              >
                <input
                  type="checkbox"
                  checked={data.selectedCategories?.includes(category.id) || false}
                  onChange={(e) => handleCategoryChange(category.id, e.target.checked)}
                  className="h-4 w-4 text-accent-color focus:ring-accent-color border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 cursor-pointer"
                />
                <span className="text-sm text-gray-900 dark:text-gray-100 cursor-pointer select-none">{category.name}</span>
              </label>
            ))}
          </div>
        )}
        {data.selectedCategories && data.selectedCategories.length > 0 && (
          <p className="text-sm text-green-600 dark:text-green-400 mt-2">
            <Check className="inline h-4 w-4 mr-1" />
            {data.selectedCategories.length} category{data.selectedCategories.length !== 1 ? 'ies' : 'y'} selected
          </p>
        )}
      </div>

      {/* Validation Summary */}
      <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md p-4">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Validation Status</h4>
        <div className="space-y-1 text-sm">
          <div className={`flex items-center ${data.selectedClient ? 'text-green-600' : 'text-red-600'}`}>
            <Check className={`h-4 w-4 mr-2 ${data.selectedClient ? 'text-green-600' : 'text-red-600'}`} />
            {data.selectedClient ? 'Client selected' : 'Client not selected'}
          </div>
          <div className={`flex items-center ${data.selectedClientLocationId ? 'text-green-600' : 'text-red-600'}`}>
            <Check className={`h-4 w-4 mr-2 ${data.selectedClientLocationId ? 'text-green-600' : 'text-red-600'}`} />
            {data.selectedClientLocationId ? 'Location selected' : 'Location not selected'}
          </div>
          <div className={`flex items-center ${data.radius ? 'text-green-600' : 'text-red-600'}`}>
            <Check className={`h-4 w-4 mr-2 ${data.radius ? 'text-green-600' : 'text-red-600'}`} />
            {data.radius ? `Radius: ${data.radius} miles` : 'Radius not selected'}
          </div>
          <div className={`flex items-center ${data.selectedCategories && data.selectedCategories.length > 0 ? 'text-green-600' : 'text-red-600'}`}>
            <Check className={`h-4 w-4 mr-2 ${data.selectedCategories && data.selectedCategories.length > 0 ? 'text-green-600' : 'text-red-600'}`} />
            {data.selectedCategories && data.selectedCategories.length > 0 
              ? `${data.selectedCategories.length} categor${data.selectedCategories.length !== 1 ? 'ies' : 'y'} selected`
              : 'No categories selected'
            }
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={handleNext}
          disabled={isSubmitting || !data.selectedClient || !data.selectedClientLocationId || !data.radius || !data.selectedCategories || data.selectedCategories.length === 0}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next Step
        </button>
      </div>

      {/* Create Client Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setFormError('');
        }}
        title="Create New Client"
        maxWidth="4xl"
      >
        <ClientCreationWizard
          onSubmit={(clientData, locationData) =>
            createClientWithLocationMutation.mutate({ client: clientData, location: locationData })
          }
          isLoading={createClientWithLocationMutation.isPending}
          error={formError}
          onCancel={() => { setShowCreateModal(false); setFormError(''); }}
        />
      </Modal>
    </div>
  );
};

export default StepClient;
